import React from "react";
import Select from "react-select";
import PropTypes from "prop-types";
import * as Filestack from "filestack-js";
import { PulseLoader } from "halogenium";
import GoogleVision from "../../../helpers/google_vision";
import Blitline from "../../../helpers/blitline";
import FilePreview from "./file_preview";
import MetadataEditor from "./metadata_editor";
import Card from "./card";
import Asset from "../../../helpers/asset";
import "./styles/workbench_editor";
import "../../../../assets/stylesheets/lib/font-awesome.scss";

// Requires inclusion of app/views/shared/_js_globals.erb
const filestackOptions = {security: BFG.filestack.signature_object, cname: BFG.filestack.cname}
const filestack = Filestack.init(BFG.filestack.API_KEY, filestackOptions)

export default class Editor extends React.Component {
  constructor(...args) {
    super(...args);
    this.downloadFile = this.downloadFile.bind(this);
    this.updateAttachment = this.updateAttachment.bind(this);
    this.updateFilename = this.updateFilename.bind(this);
    this.updateFiletype = this.updateFiletype.bind(this);
    this.updateColorSpace = this.updateColorSpace.bind(this);
    this.updateMetadata = this.updateMetadata.bind(this);
    this.updateColors = this.updateColors.bind(this);
    this.updateTags = this.updateTags.bind(this);
    this.updateText = this.updateText.bind(this);
    this.updateWidth = this.updateWidth.bind(this);
    this.updateHeight = this.updateHeight.bind(this);
    this.copyColor = this.copyColor.bind(this);
    this.copyText = this.copyText.bind(this);
    this.copyToClipBoard = this.copyToClipBoard.bind(this);
    this.extractTags = this.extractTags.bind(this);
    this.extractColors = this.extractColors.bind(this);
    this.extractMetadata = this.extractMetadata.bind(this);
    this.extractText = this.extractText.bind(this);
    this.needsConversion = this.needsConversion.bind(this);
    this.state = {
      file: null,
      filetype: null,
      metadata: {},
      edited_metadata: {},
      colors: null,
      text: null,
      tags: null,
      search_results: null,
      width: "",
      height: "",
      color_space: "srgb",
      converting: false,
      downloading: false,
      updating: false,
      download_progress: 0,
      filetype_supported: true,
      requires_thumbnail: false,
      thumbnail_url: null,
      active_color: null,
      copying_color: false,
      copying_text: false,
      search_results_open: false,
      metadata_editor_open: false,
      error: false
    };
  }

  componentDidMount() {
    let { filestack_url } = this.props;
    let url_parts = filestack_url.split("/");
    let filestack_handle = url_parts[url_parts.length - 1];
    let required_info = {
      filename: true,
      mimetype: true,
      size: true,
      height: true,
      width: true,
      exif: true
    };
    // TODO: this is the only piece of code that requires Workbench to use Filestack URLS
    filestack
      .metadata(filestack_handle, required_info)
      .then(file => {
        file.url = filestack_url;
        file.filetype = Blitline.normalizeFiletype(file.mimetype.split("/")[1]);
        file.width = file.width || "";
        file.height = file.height || "";
        if (!Blitline.webViewable(file.filetype)) {
          this.setState({ requires_thumbnail: true });
          let blitline = new Blitline(filestack_url, file.filetype);
          blitline
            .thumbnail()
            .then(thumbnail_url => {
              this.setState({ thumbnail_url });
            })
            .catch(error => {
              this.setState({ error: true });
            });
        }
        this.setState({
          file,
          filetype: file.filetype,
          width: file.width,
          height: file.height
        });
      })
      .catch(error => {
        this.setState({ error: true });
      });
  }

  copyColor(color) {
    this.setState({ copying_color: true });
    setTimeout(() => {
      this.setState({ copying_color: false });
    }, 750);
    this.copyToClipBoard(color);
  }

  copyText(text) {
    this.setState({ copying_text: true });
    setTimeout(() => {
      this.setState({ copying_text: false });
    }, 750);
    this.copyToClipBoard(text);
  }

  copyToClipBoard(value) {
    let text_area = document.createElement("textarea");
    text_area.value = value;
    document.body.appendChild(text_area);
    text_area.select();
    document.execCommand("copy");
    document.body.removeChild(text_area);
  }

  needsConversion() {
    let {
      file,
      width,
      height,
      filetype,
      color_space,
      edited_metadata
    } = this.state;
    let desired_width = width;
    let desired_height = height;
    let desired_filetype = filetype;
    let desired_color_space = color_space;
    let original_width = file.width;
    let original_height = file.height;
    let original_filetype = file.filetype;
    let original_colorspace = file.color_space || "srgb";
    let metadata_changed = Object.keys(edited_metadata).length > 0;
    let conversion_required =
      metadata_changed ||
      desired_width != original_width ||
      desired_height != desired_height ||
      desired_filetype != original_filetype ||
      desired_color_space != original_colorspace;
    return conversion_required;
  }

  extractMetadata() {
    let { file, filetype } = this.state;
    let blitline = new Blitline(file.url, filetype);
    return blitline.extractMetadata();
  }

  updateMetadata(metadata) {
    if (!!metadata && Object.keys(metadata).length > 0) {
      this.setState({ metadata });
      let color_space_key = Object.keys(metadata).find(key => {
        return key.match(/colorspace|color space/i);
      });
      if (!!color_space_key && !!metadata[color_space_key].match(/cmyk/i)) {
        this.file.color_space = "cmyk";
      } else {
        this.file.color_space = "srgb";
      }
    }
  }

  extractColors() {
    let { file } = this.state;
    let blitline = new Blitline(file.url, file.filetype);
    return blitline.extractColors();
  }

  updateColors(colors) {
    if (!!colors && colors.length > 0) {
      this.setState({ colors });
    }
  }

  extractTags() {
    let { file } = this.state;
    if (GoogleVision.hasSupportFor(file.filetype)) {
      let vision = new GoogleVision(file.url);
      return vision.extractTags();
    } else {
      let blitline = new Blitline(file.url, file.filetype);
      return blitline.extractTags();
    }
  }

  updateTags(tags) {
    if (!!tags && tags.length > 0) {
      this.setState({ tags });
    }
  }

  extractText() {
    let { file } = this.state;
    if (GoogleVision.hasSupportFor(file.filetype)) {
      let vision = new GoogleVision(file.url);
      return vision.extractText();
    } else {
      let blitline = new Blitline(file.url, file.filetype);
      return blitline.extractText();
    }
  }

  updateText(text) {
    if (!!text) {
      this.setState({ text });
    }
  }

  updateFilename(name) {
    let { file } = this.state;
    file.filename = name;
    this.setState({ file });
  }

  updateFiletype(filetype) {
    let { file, color_space } = this.state;
    file.filename = file.filename.replace(/\.\w*$/, `.${filetype}`);
    if (color_space == "cmyk" && !Blitline.cmykCompatible(filetype)) {
      this.setState({ file, filetype, color_space: "srgb" });
    } else {
      this.setState({ file, filetype });
    }
  }

  updateColorSpace(color_space) {
    let { file, filetype } = this.state;
    if (color_space == "cmyk" && !Blitline.cmykCompatible(filetype)) {
      file.filename = file.filename.replace(/\.\w*$/, ".jpg");
      this.setState({ file, filetype: "jpg", color_space });
    } else {
      this.setState({ color_space });
    }
  }

  updateWidth(width) {
    if (!!width) {
      let { file } = this.state;
      width = Math.floor(width);
      if (file.height && file.width) {
        let asset = new Asset(this.state.file);
        let minimum_width = asset.minimumWidth();
        if (width >= minimum_width) {
          this.setState({ width, height: asset.scaleHeightTo(width) });
        } else {
          this.setState({
            width: minimum_width,
            height: asset.minimumHeight()
          });
        }
      } else {
        this.setState({ width });
      }
    } else {
      this.setState({ width: "" });
    }
  }

  updateHeight(height) {
    if (!!height) {
      let { file } = this.state;
      height = Math.floor(height);
      if (file.height && file.width) {
        let asset = new Asset(file);
        let minimum_height = asset.minimumHeight();
        if (height >= minimum_height) {
          this.setState({ width: asset.scaleWidthTo(height), height });
        } else {
          this.setState({
            width: asset.minimumWidth(),
            height: minimum_height
          });
        }
      } else {
        this.setState({ height });
      }
    } else {
      this.setState({ height: "" });
    }
  }

  downloadFile() {
    if (!this.state.downloading) {
      this.setState({ converting: true });
      let {
        file,
        filetype,
        color_space,
        width,
        height,
        metadata,
        edited_metadata
      } = this.state;
      let promise = new Promise(resolve => {
        resolve(file.url);
      });
      if (this.needsConversion()) {
        let blitline = new Blitline(file.url, file.filetype);
        promise = blitline.convertTo(
          filetype,
          color_space,
          width,
          height,
          Object.assign(metadata, edited_metadata)
        );
      }
      promise
        .then(url => {
          this.setState({ converting: false, downloading: true });
          let xhr = new XMLHttpRequest();
          xhr.responseType = "blob";
          xhr.open("GET", url);
          xhr.addEventListener("progress", event => {
            if (event.total) {
              let download_progress = Math.floor(
                event.loaded / event.total * 100
              );
              this.setState({ download_progress });
            }
          });
          xhr.addEventListener("load", event => {
            if (xhr.status == 200) {
              var blob = new Blob([xhr.response], { type: file.mimetype });
              let a = document.createElement("a");
              a.style = "display: none";
              document.body.appendChild(a);
              let url = window.URL.createObjectURL(blob);
              a.href = url;
              a.download = file.filename || `sweet_file.${filetype}`;
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
              this.setState({ download_progress: 0, downloading: false });
            }
          });
          xhr.addEventListener("error", event => {
            this.setState({ download_progress: 0, downloading: false });
          });
          xhr.send(null);
        })
        .catch(error => {
          this.setState({ converting: false, error: true });
        });
    }
  }

  updateAttachment() {
    if (!this.state.updating) {
      this.setState({ updating: true });
      let {
        file,
        filetype,
        color_space,
        width,
        height,
        metadata,
        edited_metadata
      } = this.state;
      let promise = new Promise(resolve => {
        resolve(file.url);
      });
      if (this.needsConversion()) {
        let blitline = new Blitline(file.url, file.filetype);
        promise = blitline.convertTo(
          filetype,
          color_space,
          width,
          height,
          Object.assign(metadata, edited_metadata)
        );
      }
      promise
        .then(url => {
          $.ajax({
            url: `/api/v4/attachments/${this.props.attachment_key}`,
            type: "PUT",
            context: this,
            headers: { Authorization: `Bearer ${this.props.jwt}` },
            data: {
              data: { attributes: { filename: file.filename, url: url } }
            },
            success: function(response) {
              this.setState({ updating: false });
            },
            error: function(xhr) {
              this.setState({ updating: false, error: true });
            }
          });
        })
        .catch(error => {
          this.setState({ updating: false, error: true });
        });
    }
  }

  renderColors() {
    if (this.state.colors) {
      return this.state.colors.map((color, index) => {
        let { hex_color } = color.color;
        return (
          <div
            className="color"
            key={index}
            style={{ backgroundColor: hex_color }}
            onMouseEnter={() => this.setState({ active_color: color })}
            onMouseLeave={() => this.setState({ active_color: null })}
            onClick={() => this.copyColor(hex_color)}
          />
        );
      });
    }
    return <div>No colors extracted</div>;
  }

  renderColorInfo() {
    let { colors, active_color } = this.state;
    if (active_color) {
      let { color } = active_color;
      if (this.state.copying_color) {
        return (
          <div className="color-info">
            <span>Copied!</span>
          </div>
        );
      }
      return (
        <div className="color-info">
          <div className="rgb">
            <span>RGB: </span>
            {color.r}, {color.g}, {color.b}&nbsp;
          </div>
          <div className="hex">
            <span>Hex: </span>
            {color.hex_color}
          </div>
        </div>
      );
    } else if (!!colors && colors.length > 0) {
      return <div className="color-info">Hover to see color details</div>;
    }
  }

  renderMetadataEditButton() {
    if (this.state.metadata) {
      return (
        <a
          href="javascript:void(0)"
          className="card-action no-line"
          onClick={() => {
            this.setState({ metadata_editor_open: true });
          }}
        >
          <i className="fa fa-edit" /> Edit
        </a>
      );
    }
  }

  renderMetadata() {
    let { metadata } = this.state;
    if (metadata && Object.keys(metadata).length > 0) {
      return Object.keys(metadata).map((key, index) => {
        return (
          <div key={index} className="metadata">
            <span>{key}:</span> {metadata[key]}
          </div>
        );
      });
    }
    return <div>No metadata found</div>;
  }

  renderTags() {
    if (this.state.tags) {
      return this.state.tags.map((tag, index) => {
        return (
          <div key={index} className="tag">
            {tag}
          </div>
        );
      });
    }
    return <div>No tags found</div>;
  }

  renderCopyTextButton() {
    if (this.state.text && !this.state.copying_text) {
      return (
        <a
          href="javascript:void(0)"
          className="card-action no-line"
          onClick={() => this.copyText(this.state.text)}
        >
          <i className="fa fa-copy" /> Copy
        </a>
      );
    } else if (this.state.text && this.state.copying_text) {
      return (
        <a href="javascript:void(0)" className="card-action performed no-line">
          Copied!
        </a>
      );
    }
  }

  renderText() {
    if (this.state.text) {
      return (
        <div
          className="extracted-text-box"
          contentEditable="true"
          suppressContentEditableWarning="true"
        >
          {this.state.text.trim()}
        </div>
      );
    }
    return <div>No text extracted</div>;
  }

  renderConversionOptions() {
    let original_filetype = Blitline.normalizeFiletype(
      this.state.file.mimetype.split("/")[1]
    );
    let conversion_types = Blitline.conversionOptionsFor(original_filetype);
    let options = conversion_types.map(type => {
      return { value: type, label: type.toUpperCase() };
    });
    return (
      <Card title="Download As">
        <Select
          name="download_as_options"
          value={this.state.filetype || original_filetype}
          options={options}
          clearable={false}
          searchable={false}
          disabled={this.state.downloading || this.state.converting}
          onChange={option => this.updateFiletype(option.value)}
        />
      </Card>
    );
  }

  renderDownloadButton() {
    if (this.state.downloading) {
      return (
        <div className="download-progress">
          <span>{this.state.download_progress}%</span>
        </div>
      );
    } else if (this.state.converting) {
      return (
        <div className="download-progress">
          <PulseLoader
            className="processing-loader"
            color="#4F94E3"
            size="20px"
          />
        </div>
      );
    }
    return (
      <input
        className="workbench-button"
        type="submit"
        value="Download"
        onClick={this.downloadFile}
      />
    );
  }

  renderUpdateButton() {
    if (!!this.props.attachment_key) {
      if (this.state.updating) {
        return (
          <div className="download-progress">
            <PulseLoader
              className="processing-loader"
              color="#4F94E3"
              size="20px"
            />
          </div>
        );
      }
      return (
        <input
          className="workbench-button"
          type="submit"
          value="Update Attachment"
          onClick={this.updateAttachment}
        />
      );
    }
  }

  renderMetadataEditor() {
    let { metadata, metadata_editor_open } = this.state;
    if (metadata_editor_open) {
      return (
        <MetadataEditor
          metadata={metadata}
          save={edited_metadata => {
            Object.assign(metadata, edited_metadata);
            this.setState({
              metadata,
              edited_metadata,
              metadata_editor_open: false
            });
          }}
          cancel={() => this.setState({ metadata_editor_open: false })}
        />
      );
    }
  }

  render() {
    let {
      file,
      thumbnail_url,
      requires_thumbnail,
      height,
      width,
      color_space
    } = this.state;
    if (!!file) {
      let asset = new Asset(file);
      return (
        <div className="workbench-editor-component">
          <div className="workbench">
            <div className="workbench-editor">
              <div className="left-side-bar">
                <h3>Asset Info</h3>
                <Card
                  title="Color Palette"
                  content_class="color-palette"
                  request={this.extractColors}
                  onLoad={this.updateColors}
                  >
                  <div>
                    {this.renderColors()}
                    {this.renderColorInfo()}
                  </div>
                </Card>
                <Card
                  title="Extracted Text"
                  content_class="extracted-text"
                  request={this.extractText}
                  onLoad={this.updateText}
                  >
                  {this.renderCopyTextButton()}
                  {this.renderText()}
                </Card>
                <Card
                  title="Auto Tags"
                  content_class="auto-tags"
                  request={this.extractTags}
                  onLoad={this.updateTags}
                  >
                  {this.renderTags()}
                </Card>
                <Card
                  title="Metadata"
                  content_class="metadata-list"
                  request={this.extractMetadata}
                  onLoad={this.updateMetadata}
                  >
                  {this.renderMetadataEditButton()}
                  {this.renderMetadata()}
                </Card>
              </div>
              <FilePreview
                requires_thumbnail={requires_thumbnail}
                thumbnail_url={thumbnail_url}
                file={file}
                />
              {this.renderMetadataEditor()}
              <div className="right-side-bar">
                <h3>Edit Asset</h3>
                <Card title="Filename">
                  <input
                    type="text"
                    placeholder={`sweet_file.${this.state.filetype}`}
                    value={file.filename}
                    onChange={event => {
                      this.updateFilename(event.target.value);
                    }}
                    disabled={this.state.downloading || this.state.converting}
                    />
                </Card>
                <Card title="Resize">
                  <div>
                    <input
                      className="resize-width"
                      type="number"
                      placeholder="Width"
                      value={width || ""}
                      min={asset.minimumWidth()}
                      step={1}
                      onChange={event => {
                        this.updateWidth(event.target.value);
                      }}
                      disabled={this.state.downloading || this.state.converting}
                      />
                    &nbsp;×&nbsp;
                    <input
                      className="resize-height"
                      type="number"
                      placeholder="Height"
                      value={height || ""}
                      min={asset.minimumHeight()}
                      step={1}
                      onChange={event => {
                        this.updateHeight(event.target.value);
                      }}
                      disabled={this.state.downloading || this.state.converting}
                      />
                  </div>
                </Card>
                <Card title="Color Space">
                  <Select
                    name="color_space_options"
                    value={color_space}
                    options={[
                      { value: "srgb", label: "sRGB" },
                      { value: "cmyk", label: "CMYK" }
                    ]}
                    clearable={false}
                    searchable={false}
                    disabled={this.state.downloading || this.state.converting}
                    onChange={option => this.updateColorSpace(option.value)}
                    />
                </Card>
                {this.renderConversionOptions()}
                {this.renderDownloadButton()}
                {this.renderUpdateButton()}
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="workbench-editor-component">
          <div className="workbench">
            <div className="loading">Loading...</div>
          </div>
        </div>
      );
    }
  }
}

Editor.propTypes = {
  attachment_key: PropTypes.string,
  jwt: PropTypes.string,
  filestack_url: PropTypes.string.isRequired
};

Editor.defaultProps = {};
